import { Sparkles } from 'lucide-react'

import Button from '@/components/ui/Button'
import { warningPair } from '@/features/dashboard/lib/format'
import type { Check, Drawn, Fix, ScopeRows } from '@/features/mitra/api/dashboard'
import { cn } from '@/lib/utils'
import { WidgetView } from '@/screens/dashboards/components/WidgetCards'

/**
 * The strip under the formula box: the engine's check-up of what the reader
 * typed, as it arrives.
 *
 * THREE STATES AND ONE PLACE. A formula that does not read is red, with a
 * caret under where the engine stopped and its repairs as one-tap chips; one
 * that reads but measures the wrong thing is blue, with the correction and
 * both values over the reader's own book; one that is fine shows its card --
 * the value, its coverage and the formula's picture, the explanation as the
 * picture's name. A phone has no hover, so a warning's own sentence is shown
 * here, in the editor, where the reader is deciding what to write.
 */

const FIX_CHIP = cn(
  'min-h-9 rounded-full border border-destructive bg-card px-3 py-2 text-left text-body-xs text-foreground hover:bg-muted',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
)

/** The formula with a caret under the character the engine stopped at. */
function Caret({ formula, position }: { formula: string; position: number }) {
  const at = Math.max(0, Math.min(position, formula.length))
  return (
    <pre
      aria-hidden="true"
      className="mt-1 overflow-x-auto font-mono text-body-xs leading-snug text-muted-foreground"
    >
      {formula}
      {'\n'}
      {' '.repeat(at)}^
    </pre>
  )
}

export default function FormulaCheck({
  formula,
  checking,
  check,
  widget,
  holdings,
  scopes,
  onApply,
  onAsk,
}: {
  formula: string
  checking: boolean
  check: Check | null
  widget: Drawn | null
  holdings: string[]
  scopes: readonly ScopeRows[]
  onApply: (fix: Fix) => void
  /** Make a word of the formula the reader's own number; absent where there is no form. */
  /** Hand the formula to Mitra to write; absent where there is no chat. */
  onAsk?: ((question: string) => void) | undefined
}) {
  if (checking) {
    return (
      <div aria-live="polite" className="min-h-4 text-body-xs text-muted-foreground">
        Checking…
      </div>
    )
  }
  const error =
    check?.error ??
    (widget?.error ? { message: widget.error.message, position: widget.error.position ?? 0 } : null)
  if (error) {
    const [first, ...others] = check?.fixes ?? []
    return (
      <div
        aria-live="polite"
        data-role="formula-error"
        className="flex flex-col gap-2 rounded-md bg-destructive-soft px-3 py-2 text-body-s text-destructive"
      >
        <p className="sr-only">This formula does not read.</p>
        <p>{error.message}</p>
        <Caret formula={formula} position={error.position} />
        {/* A path, never only a refusal: the engine's reading of the
            reader's words first, its other repairs, a word made the
            reader's own number, and Mitra writing it. */}
        {first ? (
          <div
            data-role="fix-primary"
            className="flex flex-wrap items-center gap-2 rounded-md bg-card px-3 py-2 text-foreground"
          >
            <code title={first.label} className="min-w-0 flex-1 font-mono text-body-s break-all">
              {first.formula ?? first.label}
            </code>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                onApply(first)
              }}
            >
              Use this
            </Button>
          </div>
        ) : null}
        {others.length > 0 ? (
          <div role="group" aria-label="Fixes" className="flex flex-wrap gap-2">
            {others.map((fix) => (
              <button
                key={`${fix.label}-${fix.formula ?? ''}-${fix.kind ?? ''}`}
                type="button"
                data-role="fix-chip"
                title={fix.label}
                className={FIX_CHIP}
                onClick={() => {
                  onApply(fix)
                }}
              >
                <span className="block font-semibold">{fix.label}</span>
                {fix.formula && fix.formula !== formula ? (
                  <code className="block font-mono break-all text-muted-foreground">
                    {fix.formula}
                  </code>
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
        {onAsk ? (
          <div>
            <Button
              size="sm"
              variant="quiet"
              data-role="ask-mitra-formula"
              onClick={() => {
                onAsk(`Write a dashboard formula for: ${formula.trim()}`)
              }}
            >
              <Sparkles className="size-4" aria-hidden="true" />
              Ask Mitra to write it
            </Button>
          </div>
        ) : null}
      </div>
    )
  }
  if (!widget) return <div aria-live="polite" className="min-h-4" />
  return (
    <div
      aria-live="polite"
      data-role="formula-ok"
      className="flex min-w-0 flex-col gap-2 text-body-s"
    >
      {check?.warnings.map((warning, index) => (
        <div
          key={`${warning.code}-${String(index)}`}
          data-role="check-warning"
          className="flex flex-wrap items-center gap-2 rounded-md bg-warning-soft px-3 py-2 text-body-s text-foreground"
        >
          <span className="min-w-0 flex-1">{warning.message}</span>
          {warningPair(warning) ? (
            <span data-role="compare" className="text-label-s text-warning tabular-nums">
              {warningPair(warning)}
            </span>
          ) : null}
          {warning.fix ? (
            <button
              type="button"
              className={cn(FIX_CHIP, 'border-warning')}
              onClick={() => {
                if (warning.fix) onApply(warning.fix)
              }}
            >
              {warning.fix.label || 'Fix'}
            </button>
          ) : null}
        </div>
      ))}
      <WidgetView widget={widget} scopes={scopes} holdings={holdings} />
      {check?.unit_name ? (
        <p data-role="detected" className="text-body-xs text-muted-foreground">
          Detected: {check.unit_name}
        </p>
      ) : null}
    </div>
  )
}
